import { studentShortLabel } from "@/lib/utils-app";
import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Upload, CloudUpload, Loader2 } from "lucide-react";

// V1.51 · Contabilidad › Tasas — repositorio de tasas DGT.
const todayIso = () => new Date().toISOString().slice(0, 10);

export const TIPO_BADGE = {
  "2.1": "bg-blue-100 text-blue-800 border-blue-300",
  "4.1": "bg-violet-100 text-violet-800 border-violet-300",
};

export const ESTADO_BADGE = {
  disponible: "bg-emerald-50 text-emerald-800 border-emerald-300",
  congelada: "bg-sky-50 text-sky-800 border-sky-300",
  completada: "bg-emerald-100 text-emerald-900 border-emerald-400",
  agotada: "bg-rose-50 text-rose-800 border-rose-300",
  caducada: "bg-neutral-100 text-neutral-500 border-neutral-300 line-through",
  traslado: "bg-violet-50 text-violet-700 border-violet-300",
};

export function ExpiryChip({ dias }) {
  if (dias === null || dias === undefined) return null;
  const cls = dias < 0 ? "bg-neutral-200 text-neutral-600"
    : dias <= 30 ? "bg-red-100 text-red-800"
    : dias <= 60 ? "bg-amber-100 text-amber-800"
    : "bg-neutral-100 text-neutral-600";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold font-mono tabular-nums ${cls}`}>
      {dias < 0 ? "CADUCADA" : `${dias} días`}
    </span>
  );
}

function PdfButton({ tasa, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/dgt-tasas/${tasa.id}/pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.warning) toast.warning(data.warning, { duration: 6000 });
      else toast.success(`PDF subido a OneDrive como ${data.kind}`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="application/pdf" className="hidden"
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`tasa-pdf-btn-${tasa.id}`}
        title={tasa.onedrive_uploaded_at ? "PDF ya en OneDrive · re-subir" : "Subir PDF de la tasa"}
        className={
          "inline-flex items-center gap-1 rounded-lg px-2 h-7 text-[10px] font-bold border " +
          (tasa.onedrive_uploaded_at
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : tasa.pdf_filename
              ? "border-amber-300 bg-amber-50 text-amber-800"
              : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50")
        }
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : tasa.onedrive_uploaded_at ? <CloudUpload className="h-3 w-3" /> : <Upload className="h-3 w-3" />}
        {tasa.onedrive_uploaded_at ? "OneDrive ✓" : tasa.pdf_filename ? "PDF guardado" : "PDF"}
      </button>
    </>
  );
}

export default function TasasPanel() {
  const [tasas, setTasas] = useState([]);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ number: "", tipo: "2.1", purchase_date: todayIso(), expiry_date: "", student_id: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([api.get("/dgt-tasas"), api.get("/students")]);
      setTasas(t.data || []);
      setStudents(s.data || []);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.number.trim()) { toast.error("Indica el nº de la tasa"); return; }
    setBusy(true);
    try {
      await api.post("/dgt-tasas", form);
      toast.success("Tasa registrada en el repositorio");
      setForm({ number: "", tipo: "2.1", purchase_date: todayIso(), expiry_date: "", student_id: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const patch = async (id, updates) => {
    try { await api.patch(`/dgt-tasas/${id}`, updates); await load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta tasa del repositorio?")) return;
    try { await api.delete(`/dgt-tasas/${id}`); await load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const studentLabel = (s) => studentShortLabel(s);

  return (
    <div className="space-y-4" data-testid="tasas-panel">
      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        <h3 className="flex items-center gap-2 text-sm font-bold text-neutral-800 mb-3">
          <Ticket className="h-4 w-4 text-blue-600" /> Alta de tasa DGT
          <span className="text-[10px] font-normal text-neutral-400">Caducidad automática: compra + 6 meses (editable)</span>
        </h3>
        <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_170px_140px_140px_1fr_auto] gap-2">
          <input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })}
            placeholder="Nº tasa · ej: 481719404681" data-testid="tasa-number-input"
            className="w-full rounded-lg border border-neutral-300 px-2 h-9 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            data-testid="tasa-tipo-select"
            className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="2.1">2.1 · Nueva Admisión (2T)</option>
            <option value="4.1">4.1 · Traslado (4N)</option>
          </select>
          <input type="date" value={form.purchase_date} title="Fecha de compra"
            onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
            data-testid="tasa-purchase-input"
            className="w-full rounded-lg border border-neutral-300 px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <input type="date" value={form.expiry_date} title="Caducidad (vacío = +6 meses)"
            onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
            data-testid="tasa-expiry-input"
            className="w-full rounded-lg border border-neutral-300 px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <select value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })}
            data-testid="tasa-student-select"
            className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">— Stock (sin asignar) —</option>
            {students.map((s) => <option key={s.id} value={s.id}>{studentLabel(s)}</option>)}
          </select>
          <button type="submit" disabled={busy} data-testid="tasa-add-button"
            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 h-9 text-xs font-bold">
            <Plus className="h-3.5 w-3.5" /> Añadir
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-3 py-2">Nº tasa</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Alumno</th>
              <th className="px-3 py-2">Compra</th>
              <th className="px-3 py-2">Caducidad</th>
              <th className="px-3 py-2">Estado / Fórmula</th>
              <th className="px-3 py-2">PDF</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {tasas.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-neutral-400 italic">Sin tasas en el repositorio.</td></tr>
            )}
            {tasas.map((t) => (
              <tr key={t.id} data-testid={`tasa-row-${t.id}`}>
                <td className="px-3 py-2 font-mono font-bold text-neutral-800">{t.number}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${TIPO_BADGE[t.tipo]}`}>
                    {t.tipo} · {t.tipo_label} ({t.code})
                  </span>
                </td>
                <td className="px-3 py-2">
                  <select value={t.student_id || ""} onChange={(e) => patch(t.id, { student_id: e.target.value })}
                    data-testid={`tasa-assign-${t.id}`}
                    className="rounded-md border border-neutral-200 bg-white px-1.5 h-7 text-[11px] max-w-[160px] focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">— Stock —</option>
                    {students.map((s) => <option key={s.id} value={s.id}>{studentLabel(s)}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 font-mono tabular-nums text-neutral-600">{t.purchase_date}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <input type="date" value={t.expiry_date || ""} onChange={(e) => patch(t.id, { expiry_date: e.target.value })}
                      data-testid={`tasa-expiry-edit-${t.id}`}
                      className="rounded-md border border-neutral-200 px-1 h-7 text-[11px] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <ExpiryChip dias={t.consumption?.dias_caducidad} />
                  </div>
                </td>
                <td className="px-3 py-2 max-w-[320px]">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${ESTADO_BADGE[t.consumption?.estado] || ""}`}>
                    {t.consumption?.estado}
                  </span>
                  <p className="text-[10px] text-neutral-500 mt-0.5 leading-snug">{t.consumption?.formula}</p>
                </td>
                <td className="px-3 py-2"><PdfButton tasa={t} onDone={load} /></td>
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => remove(t.id)} data-testid={`tasa-delete-${t.id}`}
                    className="text-neutral-300 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
